import { RouteName } from '@/router';
import { useIonRouter } from '@ionic/vue';
import { useRouter, type LocationQueryRaw, type RouteLocationAsPath, type RouteLocationRaw } from 'vue-router';

const isAbsoluteAppPath = (path: string) => path === `/${RouteName.App}` || path.startsWith(`/${RouteName.App}/`);

const getLocationWithTab = (tab: string, location: RouteLocationRaw, query?: LocationQueryRaw) => {
  if (typeof location === 'string') {
    if (isAbsoluteAppPath(location)) {
      return { path: location, query };
    }
    return { path: `/${RouteName.App}/${tab}${location}`, query };
  }
  const path = location.path || '';
  if (isAbsoluteAppPath(path)) {
    return {
      ...location,
      path,
      query: { ...(query || (location as RouteLocationAsPath).query || {}) },
    };
  }
  return {
    ...location,
    path: `/${RouteName.App}/${tab}${path}`,
    query: { ...(query || (location as RouteLocationAsPath).query || {}) },
  };
};

/** Exported for unit tests — resume must pass query separately from path. */
export { getLocationWithTab };
const getCurrentTab = (fullPath: string) => {
  const tab = fullPath.split('/')?.[2] || '';
  // If there are query params, remove them
  return tab.split('?')[0] || '';
};
export function useRouterInTab() {
  const router = useIonRouter();
  const vueRouter = useRouter();
  const currentTab = getCurrentTab(vueRouter.currentRoute.value.fullPath);
  const push = (location: RouteLocationRaw, query?: LocationQueryRaw) => {
    router.push(getLocationWithTab(currentTab, location, query));
  };
  const replace = (location: RouteLocationRaw, query?: LocationQueryRaw) => {
    router.replace(getLocationWithTab(currentTab, location, query));
  };
  const canGoBack = () => {
    return router.canGoBack(); // vueRouter.currentRoute.value.fullPath.split('/').length > 1;
  };
  const back = () => {
    if (canGoBack()) {
      router.back();
    } else {
      router.replace({
        path: `/${RouteName.App}/${currentTab || RouteName.Feed}`,
      });
    }
  };
  // Hard reset to the home (feed) tab as a new root, clearing the navigation
  // history so there is nothing to go back to (e.g. after finishing a workout).
  const resetToHome = () => {
    router.navigate(`/${RouteName.App}/${RouteName.Feed}`, 'root', 'replace');
  };
  return { ...router, push, replace, canGoBack, back, resetToHome, currentRoute: vueRouter.currentRoute };
}
export type RouterInTabResult = ReturnType<typeof useRouterInTab>;
